import { CollapsibleFilters } from '../components/common/CollapsibleFilters';
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { SavedBillingViews } from "../components/billing/BillingWorkflowTools";
import { recurringError } from "../components/billing/RecurringWorkspace";
import toast from "react-hot-toast";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Input,
  Select,
  Option,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Divider,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
} from "@mui/joy";
import {
  Plus as Add,
  DotsThreeVertical as MoreVert,
  Trash as Delete,
  PencilSimple as Edit,
  Eye as Visibility,
  Play as PlayArrow,
  Pause,
  Lightning as Bolt,
} from "@phosphor-icons/react";
import { recurringInvoicesApi } from "../lib/billingApi";
import {
  formatCurrency,
  formatDate,
  getStatusColor,
  formatStatus,
} from "../lib/billingUtils";
import { ResponsiveTable } from "../components/common/ResponsiveTable";
import type {
  RecurringInvoiceTemplate,
  RecurringFrequency,
  Invoice,
} from "../types/billing";

const FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannually: "Semi-annually",
  annually: "Annually",
  custom: "Custom",
};

function formatFrequency(template: RecurringInvoiceTemplate): string {
  const base = FREQUENCY_LABELS[template.frequency] ?? template.frequency;
  if (template.frequency_interval && template.frequency_interval > 1) {
    return `${base} (every ${template.frequency_interval})`;
  }
  return base;
}

export default function RecurringInvoices() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const setPage = (value: number | ((p: number) => number)) => {
    const next = new URLSearchParams(params);
    next.set("page", String(typeof value === "function" ? value(page) : value));
    setParams(next);
  };
  const filter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    next.delete("page");
    setParams(next);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "recurring-templates",
      user?.organization,
      user?.id,
      params.toString(),
    ],
    queryFn: () =>
      recurringInvoicesApi.list({
        page,
        search: params.get("search") || "",
        queue: params.get("queue") || "",
        status: params.get("status") || "",
      }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => recurringInvoicesApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      toast.success("Recurring invoice activated");
    },
    onError: (err: any) => {
      toast.error(recurringError(err));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => recurringInvoicesApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      toast.success("Recurring invoice paused");
    },
    onError: (err: any) => {
      toast.error(recurringError(err));
    },
  });

  const runNowMutation = useMutation({
    mutationFn: (id: number) => recurringInvoicesApi.runNow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      toast.success("Invoice generated successfully");
    },
    onError: (err: any) => {
      toast.error(recurringError(err));
    },
  });

  const previewMutation = useMutation({
    mutationFn: (id: number) => recurringInvoicesApi.preview(id),
    onSuccess: (invoice) => {
      setPreviewInvoice({
        ...invoice,
        contact_name: (invoice as any).customer,
        total_amount: (invoice as any).amount,
        document_date: (invoice as any).next_generation_date,
        line_items: ((invoice as any).line_items || []).map((li: any) => ({
          ...li,
          total: Number(li.quantity) * Number(li.unit_price),
        })),
      });
      setPreviewOpen(true);
    },
    onError: (err: any) => {
      toast.error(recurringError(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      recurringInvoicesApi.update(id, { status: "cancelled" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates"] });
      toast.success("Recurring invoice cancelled");
    },
    onError: (err: any) => {
      toast.error(recurringError(err));
    },
  });

  if (error) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading recurring invoices. Please try again.
      </Alert>
    );
  }

  const templates = data?.results ?? [];

  return (
    <Box sx={{ width: "100%", p: { xs: 1.5, md: 3 } }}>
      <SavedBillingViews kind="recurring" />
      <CollapsibleFilters><Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Input
          aria-label="Search recurring invoices"
          placeholder="Search customer or template…"
          value={params.get("search") || ""}
          onChange={(e) => filter("search", e.target.value)}
        />
        <Select
          aria-label="Recurring work queue"
          value={params.get("queue") || ""}
          onChange={(_, v) => filter("queue", v || "")}
        >
          <Option value="">All queues</Option>
          <Option value="due_soon">Due in 7 days / overdue</Option>
          <Option value="approval">Awaiting approval</Option>
          <Option value="generation_failed">Generation failed</Option>
          <Option value="delivery_failed">Delivery failed</Option>
        </Select>
        <Select
          aria-label="Recurring status"
          value={params.get("status") || ""}
          onChange={(_, v) => filter("status", v || "")}
        >
          <Option value="">All statuses</Option>
          {["active", "paused", "draft", "completed", "cancelled"].map((v) => (
            <Option key={v} value={v}>
              {v}
            </Option>
          ))}
        </Select>
      </Stack></CollapsibleFilters>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          mb: { xs: 3, md: 4 },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <Box>
          <Typography level="h3">Recurring Invoices</Typography>
          <Typography level="body-md" sx={{ color: "text.secondary" }}>
            Automate repeat billing with recurring invoice templates
          </Typography>
        </Box>
        <Button
          onClick={() => navigate("new")}
          variant="solid"
          color="primary"
          startDecorator={<Add />}
        >
          New Recurring Invoice
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "center" }}>
        <Button
          variant="outlined"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </Button>
        <Typography level="body-sm">
          Page {page} · {data?.count ?? 0} templates
        </Typography>
        <Button
          variant="outlined"
          disabled={!data?.next}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Stack>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length > 0 ? (
        <ResponsiveTable<RecurringInvoiceTemplate>
          rows={templates}
          getRowKey={(t) => t.id}
          columns={[
            {
              key: "name",
              sortValue: (row) => row.name,
              header: "Name",
              mobileVariant: "header",
              render: (t) => (
                <Box>
                  <Typography level="title-sm">{t.name}</Typography>
                  {t.description && (
                    <Typography level="body-xs" color="neutral">
                      {t.description}
                    </Typography>
                  )}
                </Box>
              ),
            },
            {
              key: "customer_name",
              sortValue: (row) => row.customer_name,
              header: "Customer",
              render: (t) => (
                <Typography level="body-sm">
                  {t.customer_name || "N/A"}
                </Typography>
              ),
            },
            {
              key: "frequency",
              header: "Frequency",
              render: (t) => (
                <Typography level="body-sm">{formatFrequency(t)}</Typography>
              ),
            },
            {
              key: "next_invoice_date",
              sortValue: (row) =>
                row.next_invoice_date ? new Date(row.next_invoice_date) : null,
              header: "Next Invoice",
              render: (t) => (
                <Typography level="body-sm">
                  {formatDate(t.next_invoice_date)}
                </Typography>
              ),
            },
            {
              key: "amount",
              sortValue: (row) => Number(row.fixed_amount ?? 0),
              header: "Amount",
              render: (t) => (
                <Typography level="body-sm" fontWeight="lg">
                  {t.amount_type === "fixed"
                    ? formatCurrency(t.fixed_amount)
                    : formatStatus(t.amount_type)}
                </Typography>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (t) => (
                <Chip size="sm" color={getStatusColor(t.status)} variant="soft">
                  {formatStatus(t.status)}
                </Chip>
              ),
            },
          ]}
          rowActions={(t) => (
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{ root: { variant: "plain", size: "sm" } }}
              >
                <MoreVert />
              </MenuButton>
              <Menu size="sm" placement="bottom-end">
                <MenuItem onClick={() => navigate(`${t.id}/edit`)}>
                  <Edit /> Edit
                </MenuItem>
                {t.status === "active" ? (
                  <MenuItem onClick={() => deactivateMutation.mutate(t.id)}>
                    <Pause /> Deactivate
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => activateMutation.mutate(t.id)}>
                    <PlayArrow /> Activate
                  </MenuItem>
                )}
                <MenuItem onClick={() => runNowMutation.mutate(t.id)}>
                  <Bolt /> Run Now
                </MenuItem>
                <MenuItem onClick={() => previewMutation.mutate(t.id)}>
                  <Visibility /> Preview
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (
                      confirm(
                        "Cancel this recurring invoice? It will no longer generate invoices.",
                      )
                    ) {
                      deleteMutation.mutate(t.id);
                    }
                  }}
                  sx={{ color: "danger.main" }}
                >
                  <Delete /> Delete
                </MenuItem>
              </Menu>
            </Dropdown>
          )}
          emptyState={
            <Card variant="soft">
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Typography level="body-lg">
                  No recurring invoices found
                </Typography>
              </CardContent>
            </Card>
          }
        />
      ) : (
        <Card variant="soft">
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <Typography level="body-lg" sx={{ mb: 2 }}>
              No recurring invoices yet
            </Typography>
            <Button
              onClick={() => navigate("new")}
              startDecorator={<Add />}
              variant="solid"
            >
              Create Your First Recurring Invoice
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview Modal */}
      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <ModalDialog sx={{ maxWidth: 560, width: "100%" }}>
          <ModalClose />
          <DialogTitle>Next Invoice Preview</DialogTitle>
          <DialogContent>
            {previewInvoice ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography level="body-sm" color="neutral">
                    Customer
                  </Typography>
                  <Typography level="body-sm">
                    {previewInvoice.customer_detail
                      ? `${previewInvoice.customer_detail.first_name ?? ""} ${previewInvoice.customer_detail.last_name ?? ""}`.trim()
                      : previewInvoice.contact_name || "N/A"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography level="body-sm" color="neutral">
                    Invoice Date
                  </Typography>
                  <Typography level="body-sm">
                    {formatDate(previewInvoice.document_date)}
                  </Typography>
                </Box>
                {previewInvoice.due_date && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography level="body-sm" color="neutral">
                      Due Date
                    </Typography>
                    <Typography level="body-sm">
                      {formatDate(previewInvoice.due_date)}
                    </Typography>
                  </Box>
                )}
                {previewInvoice.line_items &&
                  previewInvoice.line_items.length > 0 && (
                    <>
                      <Divider />
                      <Stack spacing={1}>
                        {previewInvoice.line_items.map((li, idx) => (
                          <Box
                            key={li.id ?? idx}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 2,
                            }}
                          >
                            <Typography level="body-sm">
                              {li.description}
                              {li.quantity ? ` × ${li.quantity}` : ""}
                            </Typography>
                            <Typography level="body-sm" sx={{ flexShrink: 0 }}>
                              {formatCurrency(li.total)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography level="title-sm">Total</Typography>
                  <Typography level="title-sm">
                    {formatCurrency(previewInvoice.total_amount)}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography level="body-sm">No preview available.</Typography>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
